import { useEffect } from "react";

import isCurrentlyOnBrandTVDomain from "@/utils/is-currently-on-btv-domain";
import isCurrentlyOnWebinarPlusDomain from "@/utils/is-currently-on-webinarplus-domain";

const MANAGED_FAVICON_CLASS = 'managed-favicon';

/**
 * Appends the domain-appropriate <link rel="icon"> tags to <head> directly.
 * Replaces the old react-helmet <Favicons> component for the custom-JS document head.
 * Hrefs are root-relative ('/...') — Vite serves the public/ dir from the site root.
 */
export default function useFavicons(customFavicon?: string): void {
	const isBrandTV = isCurrentlyOnBrandTVDomain();
	const isWebinarPlus = isCurrentlyOnWebinarPlusDomain();

	useEffect(() => {
		// A custom favicon is already set in the html by events-app-server-3, so leave it alone.
		if (customFavicon) return;

		// Light favicon on dark mode, dark favicon on light mode.
		const favicons: { href: string; media?: string }[] = isWebinarPlus
			? [
				{ href: '/webinar-plus-light-favicon.png', media: '(prefers-color-scheme: dark)' },
				{ href: '/webinar-plus-dark-favicon.png', media: '(prefers-color-scheme: light)' },
			]
			: isBrandTV
				? [
					{ href: '/brandtv-light-favicon.png', media: '(prefers-color-scheme: dark)' },
					{ href: '/brandtv-dark-favicon.png', media: '(prefers-color-scheme: light)' },
				]
				: [{ href: '/brandlive-favicon.png' }];

		document.head.querySelectorAll(`link.${MANAGED_FAVICON_CLASS}`).forEach(el => el.remove());
		document.head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach(el => el.remove());

		const created = favicons.map(({ href, media }) => {
			const link = document.createElement('link');
			link.rel = 'icon';
			link.href = href;
			if (media) link.media = media;
			link.classList.add(MANAGED_FAVICON_CLASS);
			document.head.appendChild(link);
			return link;
		});

		return () => {
			created.forEach(link => link.remove());
		};
	}, [customFavicon, isWebinarPlus, isBrandTV]);
}
